import { useMarketGrowth } from "hooks/useRIFacPremium";
import {
    ArrowDownCircle,
    ArrowUpCircle,
    AlertCircle,
    Loader,
    TrendingUp,
} from "lucide-react";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { convertCurrency } from "utils/helpers";

interface CurrencyGrowthData {
    currency: string;
    todayAmount: number;
    yesterdayAmount: number;
    growthAmount: number;
    growthPercent: number;
    isPositiveGrowth: boolean;
}

const MarketGrowth: React.FC = () => {
    const { t } = useTranslation();
    const { marketGrowth, loading, error } = useMarketGrowth();

    const processedData = useMemo(() => {
        if (!marketGrowth || marketGrowth.length === 0) {
            return [];
        }

        const currencyGrowthData: CurrencyGrowthData[] = [];

        marketGrowth.forEach((item) => {
            if (
                item?.currency &&
                item.today_total !== null &&
                item.yesterday_total !== null
            ) {
                const currency = convertCurrency(item.currency);
                const todayAmount = item.today_total;
                const yesterdayAmount = item.yesterday_total;

                const growthAmount = todayAmount - yesterdayAmount;
                const growthPercent =
                    yesterdayAmount !== 0
                        ? (growthAmount / yesterdayAmount) * 100
                        : 0;
                const isPositiveGrowth = growthAmount >= 0;

                const existingCurrency = currencyGrowthData.find(
                    (c) => c.currency === currency,
                );

                if (existingCurrency) {
                    existingCurrency.todayAmount += todayAmount;
                    existingCurrency.yesterdayAmount += yesterdayAmount;
                    existingCurrency.growthAmount =
                        existingCurrency.todayAmount -
                        existingCurrency.yesterdayAmount;
                    existingCurrency.growthPercent =
                        existingCurrency.yesterdayAmount !== 0
                            ? (existingCurrency.growthAmount /
                                  existingCurrency.yesterdayAmount) *
                              100
                            : 0;
                    existingCurrency.isPositiveGrowth =
                        existingCurrency.growthAmount >= 0;
                } else {
                    currencyGrowthData.push({
                        currency,
                        todayAmount,
                        yesterdayAmount,
                        growthAmount,
                        growthPercent,
                        isPositiveGrowth,
                    });
                }
            }
        });

        return currencyGrowthData.sort(
            (a, b) => b.growthPercent - a.growthPercent,
        );
    }, [marketGrowth]);

    const formatCurrency = (amount: number, currencyCode: string): string => {
        return new Intl.NumberFormat(undefined, {
            style: "currency",
            currency: currencyCode === "Unknown" ? "USD" : currencyCode,
            currencyDisplay: "code",
            minimumFractionDigits: 0,
            maximumFractionDigits: Math.abs(amount) >= 1000000 ? 1 : 0,
        }).format(amount);
    };

    const formatChange = (value: number, currencyCode: string): string => {
        return `${value >= 0 ? "+" : ""}${new Intl.NumberFormat(undefined, {
            style: "currency",
            currency: currencyCode === "Unknown" ? "USD" : currencyCode,
            currencyDisplay: "code",
            minimumFractionDigits: 0,
            maximumFractionDigits: Math.abs(value) >= 1000000 ? 1 : 0,
        }).format(value)}`;
    };

    const formatPercent = (value: number): string => {
        return `${value >= 0 ? "+" : ""}${Math.abs(value).toFixed(2)}%`;
    };

    if (loading) {
        return (
            <div className="bg-white rounded-xl border border-gray-100 shadow-xs p-6 h-full flex flex-col">
                <div className="flex items-center justify-center h-full">
                    <div className="flex flex-col items-center text-gray-500">
                        <Loader className="animate-spin h-8 w-8 text-green-500 mb-3" />
                        <span className="text-sm font-medium">
                            {t("dashboard.market_growth.loading")}
                        </span>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-white rounded-xl border border-gray-100 shadow-xs p-6 h-full flex flex-col">
                <div className="flex items-center justify-center h-full">
                    <div className="flex flex-col items-center text-red-500">
                        <AlertCircle className="h-8 w-8 mb-3" />
                        <span className="text-sm font-medium">
                            {t("dashboard.market_growth.error")}
                        </span>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-white rounded-xl border border-gray-100 shadow-xs p-6 h-full flex flex-col">
            <div className="flex justify-between items-start mb-6">
                <div>
                    <h2 className="text-lg font-semibold text-gray-900">
                        {t("dashboard.market_growth.title")}
                    </h2>
                    <p className="text-sm text-gray-500 mt-1">
                        {t("dashboard.market_growth.description")}
                    </p>
                </div>
                {processedData.length > 0 && (
                    <div className="flex items-center space-x-2">
                        <span className="text-xs font-medium text-gray-500">
                            {t("dashboard.market_growth.sorted_by")}
                        </span>
                        <span className="text-xs font-medium bg-green-50 text-green-700 px-2 py-1 rounded">
                            {t("dashboard.market_growth.growth_percent")}
                        </span>
                    </div>
                )}
            </div>

            {processedData.length > 0 ? (
                <div className="flex-1 overflow-hidden flex flex-col">
                    <div className="overflow-y-auto flex-1">
                        <table className="w-full">
                            <thead>
                                <tr className="border-b border-gray-200">
                                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                        {t("dashboard.market_growth.currency")}
                                    </th>
                                    <th className="text-right py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                        {t("dashboard.market_growth.today")}
                                    </th>
                                    <th className="text-right py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                        {t("dashboard.market_growth.yesterday")}
                                    </th>
                                    <th className="text-right py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                        {t("dashboard.market_growth.change")}
                                    </th>
                                    <th className="text-right py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                        {t("dashboard.market_growth.growth")}
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {processedData.map((item, index) => (
                                    <tr
                                        key={item.currency}
                                        className={`hover:bg-gray-50 transition-colors ${index % 2 === 0 ? "bg-gray-50/50" : "bg-white"}`}>
                                        <td className="py-3 px-4">
                                            <div className="flex items-center">
                                                <span className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center mr-3">
                                                    <span className="text-xs font-medium text-green-800">
                                                        {item.currency.slice(
                                                            0,
                                                            2,
                                                        )}
                                                    </span>
                                                </span>
                                                <span className="font-medium text-gray-900">
                                                    {item.currency}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="py-3 px-4 text-right font-medium text-gray-900">
                                            {formatCurrency(
                                                item.todayAmount,
                                                item.currency,
                                            )}
                                        </td>
                                        <td className="py-3 px-4 text-right font-medium text-gray-500">
                                            {formatCurrency(
                                                item.yesterdayAmount,
                                                item.currency,
                                            )}
                                        </td>
                                        <td className="py-3 px-4 text-right">
                                            <span
                                                className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                                    item.isPositiveGrowth
                                                        ? "bg-green-50 text-green-700"
                                                        : "bg-red-50 text-red-700"
                                                }`}>
                                                {formatChange(
                                                    item.growthAmount,
                                                    item.currency,
                                                )}
                                            </span>
                                        </td>
                                        <td className="py-3 px-4 text-right">
                                            <div className="flex items-center justify-end">
                                                {item.isPositiveGrowth ? (
                                                    <ArrowUpCircle
                                                        className="text-green-500 mr-2"
                                                        size={16}
                                                    />
                                                ) : (
                                                    <ArrowDownCircle
                                                        className="text-red-500 mr-2"
                                                        size={16}
                                                    />
                                                )}
                                                <span
                                                    className={`font-semibold ${
                                                        item.isPositiveGrowth
                                                            ? "text-green-600"
                                                            : "text-red-600"
                                                    }`}>
                                                    {formatPercent(
                                                        item.growthPercent,
                                                    )}
                                                </span>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            ) : (
                <div className="flex-1 flex flex-col items-center justify-center text-gray-400">
                    <TrendingUp className="h-10 w-10 mb-3" />
                    <p className="font-medium">
                        {t("dashboard.market_growth.empty_title")}
                    </p>
                    <p className="text-sm mt-1">
                        {t("dashboard.market_growth.empty_description")}
                    </p>
                </div>
            )}
        </div>
    );
};

export default MarketGrowth;
